import { arrayMove } from "@dnd-kit/sortable";
import { ItemPosition } from "../components/controls/Position";
import { getLayerableCollectionItems } from "../helpers";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";
import {
  CustomText,
  Header,
  Logo,
  MediaItem,
  Avatar,
  Shape,
  SubtitlesItem,
  TemplateType,
  TitleItem,
  ParameteredText,
  Table,
  MainImage,
} from "../types";

type ItemToMove =
  | MediaItem
  | SubtitlesItem
  | TitleItem
  | CustomText
  | Shape
  | Avatar
  | Logo
  | ParameteredText
  | Table;

type TemplateItem = ItemToMove | Header;

export const useChangeLayer = (itemToMove?: ItemToMove) => {
  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const {
    presenter,
    textStyle,
    titleStyle,
    images,
    videos,
    customTexts,
    shapes,
    header,
    logo,
    view,
    figure,
    mainImage,
    slideLayoutShapes,
    table,
    templateState,
    setTemplateState,
    parameteredTexts,
  } = useTemplateEditorStore((store) => ({
    presenter: store.templateState.presenter,
    textStyle: store.templateState.textStyle,
    titleStyle: store.templateState.titleStyle,
    images: store.templateState.images,
    videos: store.templateState.videos,
    customTexts: store.templateState.customTexts,
    shapes: store.templateState.shapes,
    header: store.templateState.header,
    logo: store.templateState.logo,
    view: store.currentChapter.type,
    parameteredTexts: store.templateState.parameteredTexts,
    figure: store.templateState.figure,
    mainImage: store.templateState.mainImage,
    slideLayoutShapes: store.templateState.slideLayoutShapes,
    table: store.templateState.table,
    templateState: store.templateState,
    setTemplateState: store.updateTemplateState,
  }));

  const getSortedStoreItems = () => {
    const storeItems = [
      view === TemplateType.CHAPTER && header.background ? header : undefined,
      view === TemplateType.CHAPTER && logo.showLogo ? logo : undefined,
      (view === TemplateType.CHAPTER || view === TemplateType.TRANSITION) &&
      presenter.presenter
        ? presenter
        : undefined,
      ...getLayerableCollectionItems(templateState),
      view === TemplateType.CHAPTER && textStyle.showSubtitles
        ? textStyle
        : undefined,
      view === TemplateType.TRANSITION && titleStyle.showTitle
        ? titleStyle
        : undefined,
    ].filter((item): item is TemplateItem => item !== undefined);

    return storeItems.toSorted((a, b) => a.layerIndex - b.layerIndex);
  };

  const isSameItem = (item: TemplateItem, target: ItemToMove): boolean => {
    // Items with id can be compared by id
    if ("id" in item && "id" in target) {
      return item.id === target.id;
    }
    // Items without id (Logo, Header, etc.) are compared by reference
    return item === target;
  };

  // Reconstruct the state with updated layerIndex values
  const getUpdatedStoreItems = (sortedStoreItems: TemplateItem[]) => {
    const updatedImages = images.map((img) => {
      const updatedItem = sortedStoreItems.find(
        (item): item is MediaItem => "id" in item && item.id === img.id
      );
      return updatedItem ?? img;
    });

    const updatedVideos = videos.map((vid) => {
      const updatedItem = sortedStoreItems.find(
        (item): item is MediaItem => "id" in item && item.id === vid.id
      );
      return updatedItem ?? vid;
    });

    const updatedCustomTexts = customTexts.map((text) => {
      const updatedItem = sortedStoreItems.find(
        (item): item is CustomText => "id" in item && item.id === text.id
      );
      return updatedItem ?? text;
    });

    const updatedShapes = shapes.map((shape) => {
      const updatedItem = sortedStoreItems.find(
        (item): item is Shape => "id" in item && item.id === shape.id
      );
      return updatedItem ?? shape;
    });

    const updatedHeader =
      view === TemplateType.CHAPTER && header.background
        ? (sortedStoreItems.find(
            (item): item is Header =>
              "banner" in item && item.banner === header.banner
          ) ?? header)
        : header;

    const updatedLogo =
      view === TemplateType.CHAPTER && logo.showLogo
        ? (sortedStoreItems.find((item): item is Logo => "showLogo" in item) ??
          logo)
        : logo;

    const updatedTextStyle =
      view === TemplateType.CHAPTER && textStyle.showSubtitles
        ? (sortedStoreItems.find(
            (item): item is SubtitlesItem => "showSubtitles" in item
          ) ?? textStyle)
        : textStyle;

    const updatedTitleStyle =
      view === TemplateType.TRANSITION && titleStyle.showTitle
        ? (sortedStoreItems.find(
            (item): item is TitleItem => "showTitle" in item
          ) ?? titleStyle)
        : titleStyle;

    const updatedPresenter =
      (view === TemplateType.CHAPTER || view === TemplateType.TRANSITION) &&
      presenter.presenter
        ? (sortedStoreItems.find(
            (item): item is Avatar =>
              "presenter" in item && item.presenter === presenter.presenter
          ) ?? presenter)
        : presenter;

    const updatedParameteredTexts = parameteredTexts?.map((text) => {
      const updatedItem = sortedStoreItems.find(
        (item): item is ParameteredText => "id" in item && item.id === text.id
      );
      return updatedItem ?? text;
    });

    const updatedFigure = figure
      ? {
          number:
            sortedStoreItems.find(
              (item): item is ParameteredText =>
                "id" in item && item.id === figure.number.id
            ) ?? figure.number,
          units:
            sortedStoreItems.find(
              (item): item is ParameteredText =>
                "id" in item && item.id === figure.units?.id
            ) ?? figure.units,
        }
      : undefined;

    const updatedMainImage = mainImage
      ? (sortedStoreItems.find(
          (item): item is MainImage => "id" in item && item.id === mainImage.id
        ) ?? mainImage)
      : undefined;

    const updatedSlideLayoutShapes = slideLayoutShapes?.map((shape) => {
      const updatedItem = sortedStoreItems.find(
        (item): item is Shape => "id" in item && item.id === shape.id
      );
      return updatedItem ?? shape;
    });

    const updatedTable = table
      ? (sortedStoreItems.find(
          (item): item is Table => "id" in item && item.id === table.id
        ) ?? table)
      : undefined;

    return {
      updatedImages,
      updatedVideos,
      updatedCustomTexts,
      updatedShapes,
      updatedHeader,
      updatedLogo,
      updatedTextStyle,
      updatedTitleStyle,
      updatedPresenter,
      updatedParameteredTexts,
      updatedFigure,
      updatedMainImage,
      updatedSlideLayoutShapes,
      updatedTable,
    };
  };

  const updateStoreItems = (sortedStoreItems: TemplateItem[]) => {
    const updatedStoreItems = getUpdatedStoreItems(sortedStoreItems);

    setTemplateState({
      ...templateState,
      images: updatedStoreItems.updatedImages,
      videos: updatedStoreItems.updatedVideos,
      customTexts: updatedStoreItems.updatedCustomTexts,
      shapes: updatedStoreItems.updatedShapes,
      header: updatedStoreItems.updatedHeader,
      logo: updatedStoreItems.updatedLogo,
      textStyle: updatedStoreItems.updatedTextStyle,
      titleStyle: updatedStoreItems.updatedTitleStyle,
      presenter: updatedStoreItems.updatedPresenter,
      parameteredTexts: updatedStoreItems.updatedParameteredTexts,
      figure: updatedStoreItems.updatedFigure,
      mainImage: updatedStoreItems.updatedMainImage,
      slideLayoutShapes: updatedStoreItems.updatedSlideLayoutShapes,
      table: updatedStoreItems.updatedTable,
    });
    addTemplateUpdate();
    return updatedStoreItems;
  };

  const moveLayer = (key: ItemPosition) => {
    if (!itemToMove) return;
    let sortedStoreItems = getSortedStoreItems();

    const itemToMoveIndex = sortedStoreItems.findIndex((item) =>
      isSameItem(item, itemToMove)
    );

    if (itemToMoveIndex === -1) return;

    const currentItem = sortedStoreItems[itemToMoveIndex] as TemplateItem;

    // if the item is the last item, return
    if (
      (key === ItemPosition.moveToFront ||
        key === ItemPosition.moveOneLayerForward) &&
      currentItem.layerIndex === sortedStoreItems.at(-1)?.layerIndex
    ) {
      return;
    }
    // if the item is the first item, return
    if (
      (key === ItemPosition.moveOneLayerBack ||
        key === ItemPosition.moveToBack) &&
      itemToMoveIndex === 0
    ) {
      return;
    }

    if (key === ItemPosition.moveToFront) {
      const maxLayerIndex = sortedStoreItems.at(-1)?.layerIndex;
      if (maxLayerIndex === undefined) {
        return;
      }
      sortedStoreItems[itemToMoveIndex] = {
        ...currentItem,
        layerIndex: maxLayerIndex + 1,
      };
    }

    const nextItem = sortedStoreItems[itemToMoveIndex + 1];
    if (key === ItemPosition.moveOneLayerForward && nextItem) {
      const currentLayerIndex = currentItem.layerIndex;
      const nextLayerIndex = nextItem.layerIndex;

      sortedStoreItems[itemToMoveIndex] = {
        ...currentItem,
        layerIndex: nextLayerIndex,
      };
      sortedStoreItems[itemToMoveIndex + 1] = {
        ...nextItem,
        layerIndex: currentLayerIndex,
      };
    }

    const previousItem = sortedStoreItems[itemToMoveIndex - 1];
    if (key === ItemPosition.moveOneLayerBack && previousItem) {
      const currentLayerIndex = currentItem.layerIndex;
      const previousLayerIndex = previousItem.layerIndex;

      sortedStoreItems[itemToMoveIndex] = {
        ...currentItem,
        layerIndex: previousLayerIndex,
      };
      sortedStoreItems[itemToMoveIndex - 1] = {
        ...previousItem,
        layerIndex: currentLayerIndex,
      };
    }

    if (key === ItemPosition.moveToBack) {
      // Move current item to the back
      sortedStoreItems[itemToMoveIndex] = {
        ...currentItem,
        layerIndex: 1,
      };

      // Increment all other items' layerIndex
      sortedStoreItems = sortedStoreItems.map((item, index) => {
        if (index < itemToMoveIndex) {
          return { ...item, layerIndex: item.layerIndex + 1 };
        }
        return item;
      });
    }

    return updateStoreItems(sortedStoreItems);
  };

  /**
   * Reorders the layer stack to match a drag-and-drop result. `orderedItems`
   * must be the full back-to-front list of layer items (e.g. from
   * `getLayerDescriptors`); `fromIndex`/`toIndex` are positions within it.
   *
   * Unlike `moveLayer` (single-step / front / back), this handles arbitrary
   * jumps in one store write: it moves the item then reassigns contiguous
   * `layerIndex` values across the whole stack so z-order can never collide.
   */
  const reorderLayers = (
    orderedItems: TemplateItem[],
    fromIndex: number,
    toIndex: number
  ) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= orderedItems.length ||
      toIndex >= orderedItems.length
    ) {
      return;
    }

    const reindexed = arrayMove(orderedItems, fromIndex, toIndex).map(
      (item, index) => ({ ...item, layerIndex: index + 1 })
    );

    return updateStoreItems(reindexed);
  };

  const newHighestLayerIndex =
    (getSortedStoreItems().at(-1)?.layerIndex ?? 0) + 1;

  return { moveLayer, reorderLayers, newHighestLayerIndex };
};
